import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaImages, FaPlus, FaSortAmountDown, FaSortAmountUp } from 'react-icons/fa';
import { FiEdit2 } from 'react-icons/fi';
import { HiSearch } from 'react-icons/hi';
import classNames from 'classnames';
import { toast } from 'react-toastify';

import { PhotoAlbum } from '../../../../types/photoAlbum';
import photoAlbumService from '../../../../services/photoAlbumService';
import useSelectedCommunity from '../../../../hooks/community/useSelectedCommunity';
import { CommunityRole } from '../../../../types/community';
import CommunityHeader from '../../../../components/headers/CommunityHeader';
import CreatePhotoAlbumModal from '../../../../components/modals/CreatePhotoAlbumModal';
import EditPhotoAlbumModal from '../../../../components/modals/EditPhotoAlbumModal';
import HorizontalSponsorsList from '../../../../components/misc/HorizontalSponsorsList';

interface PhotoAlbumCardProps {
  album: PhotoAlbum;
  onClick: (albumId: string) => void;
  onEdit?: (albumId: string) => void;
}

function PhotoAlbumCard({ album, onClick, onEdit }: PhotoAlbumCardProps) {
  const { t } = useTranslation();

  return (
    <div
      onClick={() => onClick(album.id)}
      className="relative mx-auto w-full max-w-[18rem] cursor-pointer overflow-hidden rounded-lg bg-secondary-50 p-2">
      {onEdit && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(album.id);
          }}
          aria-label={t('general.edit')}
          className="absolute right-4 top-4 z-10 rounded-md bg-secondary-50 p-1">
          <FiEdit2 className="h-4 w-4 text-primary" />
        </button>
      )}
      {/* Image Section */}
      {album.coverUrl ? (
        <img src={album.coverUrl} alt={album.title} className="aspect-square w-full rounded-md object-cover" />
      ) : (
        <div className="flex aspect-square items-center justify-center rounded-md bg-secondary-200">
          <FaImages className="text-secondary-300 h-10 w-10" />
        </div>
      )}

      {/* Content Section */}
      <div className="w-full min-w-0 p-4">
        <h3 className="truncate text-lg font-semibold text-primary md:text-2xl">{album.title}</h3>

        {album.description && <p className="text-secondary-600 line-clamp-1 text-xs">{album.description}</p>}

        <span className="text-secondary-600 mt-1 block text-xs">
          {album.photoCount} {album.photoCount === 1 ? t('general.photo_one') : t('general.photo_other')}
        </span>

        {album.openToUpload && (
          <span className="mt-1 inline-block rounded-full bg-success/20 px-2 py-0.5 text-xs font-medium text-success">
            {t('page.dashboard.albums.openToUpload')}
          </span>
        )}
      </div>
    </div>
  );
}

export default function CommunityAlbumsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const community = useSelectedCommunity();

  const [loading, setLoading] = useState(false);
  const [albums, setAlbums] = useState<PhotoAlbum[]>([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'title' | 'date' | 'photoCount'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [openCreateAlbumModal, setOpenCreateAlbumModal] = useState(false);

  const canCreateAlbums = useMemo(() => {
    if (!community) return false;

    return community.role === CommunityRole.ADMIN || !!community.memberPermissions?.PHOTO_ALBUMS?.CREATE;
  }, [community]);

  const canEditAlbums = useMemo(() => {
    if (!community) return false;

    return community.role === CommunityRole.ADMIN || !!community.memberPermissions?.PHOTO_ALBUMS?.UPDATE;
  }, [community]);

  const loadAlbums = () => {
    if (!community?.id) return;

    setLoading(true);
    photoAlbumService
      .getPhotoAlbums(community.id, {
        search: search || undefined,
        sortBy,
        sortOrder,
      })
      .then((data) => setAlbums(data))
      .catch((error) => {
        console.error('Failed to load albums:', error);
        if (!navigator.onLine) {
          toast.error(t('error.offline'));
        } else {
          toast.error(t('page.createPhotoAlbum.errors.loadFailed'));
        }
      })
      .finally(() => setLoading(false));
  };

  // Fetch albums when component mounts or community changes
  useEffect(() => {
    loadAlbums();
  }, [community?.id, search, sortBy, sortOrder]);

  // Filter and sort albums locally for immediate feedback
  const filteredAlbums = useMemo(() => {
    let filtered = [...albums];

    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(
        (album) =>
          album.title.toLowerCase().includes(searchLower) || album.description?.toLowerCase().includes(searchLower),
      );
    }

    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;

      if (sortBy === 'title') {
        comparison = a.title.localeCompare(b.title);
      } else if (sortBy === 'photoCount') {
        comparison = a.photoCount - b.photoCount;
      } else if (sortBy === 'date') {
        // Assuming albums have a date field or use id as proxy
        comparison = (a.id as any) - (b.id as any);
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [albums, search, sortBy, sortOrder]);

  const isEmpty = filteredAlbums.length === 0 && !loading;

  const handleCreateAlbum = () => {
    if (!canCreateAlbums) return;

    if (community?.id) {
      setOpenCreateAlbumModal(true);
    }
  };

  const [editingAlbumId, setEditingAlbumId] = useState<string | null>(null);
  const [showEditAlbumModal, setShowEditAlbumModal] = useState(false);

  const handleAlbumClick = (albumId: string) => {
    navigate(`/community/albums/${albumId}`);
  };

  const handleEditAlbum = (albumId: string) => {
    setEditingAlbumId(albumId);
    setShowEditAlbumModal(true);
  };

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  if (!community) {
    return (
      <div className="flex justify-center py-12">
        <p className="text-secondary-600">{t('page.community.selectCommunity')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <CommunityHeader
        title={t('page.dashboard.albums.title')}
        action={
          !isEmpty && canCreateAlbums ? (
            <div className="w-full sm:w-auto">
              <button
                type="button"
                onClick={handleCreateAlbum}
                className="flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-white transition-colors">
                <FaPlus />
                <span className="truncate">{t('page.dashboard.albums.create')}</span>
              </button>
            </div>
          ) : undefined
        }
      />

      <HorizontalSponsorsList placement="PHOTO_ALBUMS" />

      {!isEmpty && (
        <>
          {/* Search Bar and Sort Controls - styled like AgendaListView */}
          <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                <HiSearch className="h-5 w-5 text-primary" />
              </div>
              <input
                type="search"
                placeholder={t('general.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-lg !bg-secondary-50 py-2 pl-10 pr-4 text-primary focus:outline-none focus:ring-0 sm:w-1/3"
              />
            </div>
            <div className="flex rounded-lg bg-secondary-50 p-1">
              <button
                type="button"
                onClick={() => setSortBy('date')}
                className={classNames('rounded-md px-4 py-2 font-medium text-primary transition-all', {
                  '!bg-secondary': sortBy === 'date',
                })}>
                {t('general.date')}
              </button>
              <button
                type="button"
                onClick={() => setSortBy('title')}
                className={classNames('rounded-md px-4 py-2 font-medium text-primary transition-all', {
                  '!bg-secondary': sortBy === 'title',
                })}>
                {t('general.title')}
              </button>
              <button
                type="button"
                onClick={() => setSortBy('photoCount')}
                className={classNames('rounded-md px-4 py-2 font-medium text-primary transition-all', {
                  '!bg-secondary': sortBy === 'photoCount',
                })}>
                {t('general.photoCount')}
              </button>
              <div className="mx-1 my-1 w-px bg-details/20" />
              <button
                type="button"
                onClick={toggleSortOrder}
                className="rounded-md px-3 py-2 font-medium text-primary transition-all"
                title={sortOrder === 'asc' ? t('general.ascending') : t('general.descending')}>
                {sortOrder === 'asc' ? <FaSortAmountUp /> : <FaSortAmountDown />}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="text-lg text-details/70">{t('page.dashboard.albums.loading')}</div>
        </div>
      ) : filteredAlbums.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-4 py-12">
          <FaImages className="mb-4 h-16 w-16 text-primary" />
          <h3 className="text-xl font-semibold text-primary">{t('page.community.empty.albums')}</h3>
          {!search && canCreateAlbums && (
            <button
              type="button"
              onClick={handleCreateAlbum}
              className="mt-4 w-full items-center justify-center gap-2 rounded-lg bg-primary px-6 py-2 text-white sm:inline-flex">
              <FaPlus />
              <span className="truncate">{t('page.dashboard.albums.createFirst')}</span>
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(14rem,18rem))] justify-center gap-4">
          {filteredAlbums.map((album) => (
            <PhotoAlbumCard
              key={album.id}
              album={album}
              onClick={handleAlbumClick}
              onEdit={canEditAlbums ? handleEditAlbum : undefined}
            />
          ))}
        </div>
      )}

      <CreatePhotoAlbumModal
        open={openCreateAlbumModal}
        setOpen={setOpenCreateAlbumModal}
        onSuccess={() => {
          setOpenCreateAlbumModal(false);
          loadAlbums();
        }}
      />

      <EditPhotoAlbumModal
        open={showEditAlbumModal}
        setOpen={setShowEditAlbumModal}
        album={editingAlbumId ? filteredAlbums.find((a) => a.id === editingAlbumId) || null : null}
        onSave={() => {
          setShowEditAlbumModal(false);
          setEditingAlbumId(null);
          loadAlbums();
        }}
      />
    </div>
  );
}
